import { Controller } from '@hotwired/stimulus'

const FILTER_NAME_PREFIX = 'filter['

/**
 * Opens "Delete Flights" against the filters the flights table is showing.
 *
 * The datatable applies its filters over AJAX and never reloads the page, so
 * the filter inputs are read off the page here and handed to the modal as query
 * params. What the confirmation counts is then the set the table is showing,
 * and the same params travel on to the deletion itself.
 *
 * The modal opens on the click rather than on the counts coming back, since counting a big set takes long enough to look like a missed click.
 *
 * An empty list has nothing to delete, so the button is left disabled rather than opening a modal that can only say so.
 * The table's own free-text search stands the button down as well, since the search narrows the rows on show without narrowing what the deletion takes, and a button that would delete more than the table is showing is not one to leave armed.
 * recordsTotal is the set behind the named filters, which is the set the deletion works from, and recordsFiltered is what the search has left of it.
 */
export default class extends Controller {
  static targets = ['loading', 'button']
  static values = { url: String, frameId: String }

  // The table has fetched its rows again, which is the only moment the button hears what it would be taking.
  update ({ detail: { recordsTotal, recordsFiltered } }) {
    if (this.deletionPending) return

    const behindTheFilters = Number(recordsTotal)
    const leftOnTheTable = Number(recordsFiltered)

    this.disableButton(behindTheFilters === 0 || leftOnTheTable !== behindTheFilters)
  }

  disableButton (disabled) {
    this.buttonTarget.disabled = disabled
    this.buttonTarget.classList.toggle('disabled', disabled)
    this.buttonTarget.setAttribute('aria-disabled', String(disabled))
  }

  // A deletion still running has already disabled the button, and it stays that way until whatever renders it next.
  get deletionPending () {
    return this.buttonTarget.dataset.deletionPending === 'true'
  }

  open () {
    const frame = document.getElementById(this.frameIdValue)
    if (!frame) return

    this.showLoading(frame)
    this.openModal()
    this.keepDismissedModalClosed(frame)

    // Cleared first so a second open with the same filters still loads the
    // frame: an unchanged src is a no-op, and the modal opens on frame load.
    frame.removeAttribute('src')
    frame.src = `${this.urlValue}?${this.filterParams()}`
  }

  // Whatever the last open left behind is not what this one is about to disclose.
  showLoading (frame) {
    if (!this.hasLoadingTarget) return

    frame.replaceChildren(this.loadingTarget.content.cloneNode(true))
  }

  // The modal opens itself again on frame load, which a shown modal takes as the no-op it is.
  openModal () {
    const modal = this.modal
    if (!modal) return

    this.application
      .getControllerForElementAndIdentifier(modal, 'turbo-modal')
      ?.open()
  }

  // A modal closed while the counting was still out stays closed when the counts land on it.
  keepDismissedModalClosed (frame) {
    frame.addEventListener('turbo:frame-load', (event) => {
      if (!this.modal?.classList.contains('show')) event.stopPropagation()
    }, { once: true })
  }

  // The filter inputs of the form this button sits in, named as the flights page submits them.
  filterParams () {
    const params = new URLSearchParams()
    const form = this.element.closest('form')
    if (!form) return params.toString()

    Array.from(form.elements).forEach((input) => {
      if (!input.name?.startsWith(FILTER_NAME_PREFIX)) return

      const value = $(input).val()

      if (Array.isArray(value)) {
        value.filter(Boolean).forEach((entry) => params.append(input.name, entry))
      } else if (value) {
        params.append(input.name, value)
      }
    })

    return params.toString()
  }

  get modal () {
    return document.getElementById(`${this.frameIdValue}__modal`)
  }
}
